import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { stripApiSuffix } from '../utils/apiBaseUrl';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3050/api';
const AUTH_BASE_URL = stripApiSuffix(API_BASE_URL);

/**
 * Authentication Context
 * Manages user authentication state and provides auth-related functions.
 * Stores both access token (short-lived, 15 min) and refresh token (long-lived, 30 days).
 * Automatically refreshes access tokens when they expire.
 */
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing auth token on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const userId = localStorage.getItem('user_id');
    const userEmail = localStorage.getItem('user_email');

    if (token && userId) {
      setUser({
        id: userId,
        email: userEmail,
        token: token,
        refreshToken: refreshToken,
      });
    }
    setLoading(false);
  }, []);

  /**
   * Refresh the access token using the stored refresh token.
   * Returns the new access token on success, or null on failure.
   */
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    console.log('[Auth] refreshAccessToken called, refresh_token:', refreshToken ? 'present' : 'MISSING');
    if (!refreshToken) {
      return null;
    }

    try {
      const url = `${AUTH_BASE_URL}/auth/refresh`;
      console.log('[Auth] Calling refresh endpoint:', url);
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      console.log('[Auth] Refresh response status:', response.status);

      if (!response.ok) {
        const errorBody = await response.text();
        console.error('[Auth] Refresh failed:', response.status, errorBody);
        return null;
      }

      const data = await response.json();
      console.log('[Auth] Refresh response:', { success: data.success, hasToken: !!data.token, hasRefreshToken: !!data.refresh_token });
      if (data.success && data.token) {
        localStorage.setItem('auth_token', data.token);
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }

        setUser(prev => prev ? {
          ...prev,
          token: data.token,
          refreshToken: data.refresh_token || prev.refreshToken,
        } : null);

        return data.token;
      }

      return null;
    } catch (err) {
      console.error('[Auth] Refresh exception:', err);
      return null;
    }
  }, []);

  /**
   * Get a valid access token, refreshing if needed.
   * Returns null if the user must re-authenticate.
   */
  const getValidToken = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    console.log('[Auth] getValidToken called, auth_token:', token ? 'present (' + token.length + ' chars)' : 'MISSING');
    if (!token) return null;

    // Quick JWT expiry check (decode payload without verification)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const now = Date.now();
      const timeLeft = expiresAt - now;
      console.log('[Auth] Token expiry check: expires in', Math.round(timeLeft / 1000), 'seconds, expired:', timeLeft <= 0);
      // Refresh if token expires within 60 seconds
      if (now > expiresAt - 60000) {
        console.log('[Auth] Token expired or expiring soon, refreshing...');
        const newToken = await refreshAccessToken();
        return newToken;
      }
    } catch (err) {
      console.error('[Auth] Token decode error:', err);
      const newToken = await refreshAccessToken();
      return newToken;
    }

    console.log('[Auth] Token is still valid, returning it');
    return token;
  }, [refreshAccessToken]);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${AUTH_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      const requestId = data.request_id || response.headers.get('X-Request-ID');

      if (!response.ok) {
        return {
          success: false,
          error: data.detail || 'Login failed. Please try again.',
          requestId,
        };
      }

      // Store auth data (both access and refresh tokens)
      console.log('[Auth] Login success — storing tokens. Has access_token:', !!data.token, 'Has refresh_token:', !!data.refresh_token);
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user_id', data.user_id);
      localStorage.setItem('user_email', email);
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }

      setUser({
        id: data.user_id,
        email: email,
        token: data.token,
        refreshToken: data.refresh_token || null,
      });

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: 'Unable to connect to the server. Please check your connection and try again.',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_email');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    refreshAccessToken,
    getValidToken,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
